import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

function formatErr(detail) {
    if (!detail) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin(role) {
    localStorage.setItem("flow_pending_role", role);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
    const { t } = useLang();
    const { register } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("student");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await register({ email, password, name, role });
            nav("/dashboard", { replace: true });
        } catch (e) {
            setErr(formatErr(e.response?.data?.detail) || t.auth.taken);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            className="min-h-[80vh] flex items-center justify-center px-5"
            data-testid="signup-page"
        >
            <div className="w-full max-w-md card p-8">
                <h1 className="font-display text-3xl text-white uppercase">
                    {t.auth.signupHead}
                </h1>
                <p className="font-brush text-[var(--coral)] text-3xl -rotate-3 inline-block mt-1">
                    join the lab
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <Field label={t.auth.name}>
                        <input
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input-line mt-1"
                            data-testid="signup-name"
                        />
                    </Field>
                    <Field label={t.auth.email}>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-line mt-1"
                            data-testid="signup-email"
                        />
                    </Field>
                    <Field label={t.auth.password}>
                        <input
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-line mt-1"
                            data-testid="signup-password"
                        />
                    </Field>
                    <Field label={t.auth.role}>
                        <div className="flex gap-2 mt-2">
                            {["student", "teacher"].map((r) => (
                                <button
                                    type="button"
                                    key={r}
                                    onClick={() => setRole(r)}
                                    data-testid={`signup-role-${r}`}
                                    className={
                                        "px-4 py-2 font-mono uppercase text-xs tracking-widest border " +
                                        (role === r
                                            ? "border-[var(--lime)] text-[var(--lime)] bg-[var(--lime)]/5"
                                            : "border-[var(--border)] text-[var(--muted)]")
                                    }
                                >
                                    {r === "student"
                                        ? t.auth.student
                                        : t.auth.teacher}
                                </button>
                            ))}
                        </div>
                    </Field>
                    {err && (
                        <p
                            className="font-mono text-sm text-[var(--coral)]"
                            data-testid="signup-error"
                        >
                            {err}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-coral w-full justify-center"
                        data-testid="signup-submit"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        {t.auth.submitSignup}
                    </button>
                </form>

                <div className="flex items-center gap-3 my-6">
                    <span className="flex-1 h-px bg-[var(--border)]" />
                    <span className="font-mono text-xs text-[var(--muted)] uppercase tracking-widest">
                        {t.auth.or}
                    </span>
                    <span className="flex-1 h-px bg-[var(--border)]" />
                </div>

                <button
                    type="button"
                    onClick={() => googleLogin(role)}
                    className="btn-ghost w-full justify-center"
                    data-testid="signup-google"
                >
                    <span className="text-[var(--lime)] mr-2">G</span>
                    {t.auth.google}
                </button>

                <Link
                    to="/login"
                    className="lnk mt-6 inline-block font-mono text-sm"
                    data-testid="switch-login"
                >
                    {t.auth.switchToLogin}
                </Link>
            </div>
        </main>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                {label}
            </span>
            {children}
        </label>
    );
}
